import { createFileRoute, Link, notFound, type ErrorComponentProps } from '@tanstack/react-router'
import { z } from 'zod'
import { GalleryContent } from '@/features/accommodation/gallery/components/GalleryContent'
import { accommodationDetailQueryOptions } from '@/api/accommodation/hooks'
import { Button } from '@/components/ui/button'
import { HttpError } from '@/api/http'
import { SITE_NAME, getSiteOrigin } from '@/lib/seo/siteMeta'

const galleryParamsSchema = z.object({
  slug: z.string().min(1),
  unitSlug: z.string().min(1),
})

function GalleryErrorComponent({ error }: ErrorComponentProps) {
  let title = 'Chyba při načítání'
  let message = 'Nepodařilo se načíst galerii. Zkuste to prosím znovu.'

  if (error instanceof HttpError && error.status === 404) {
    title = 'Ubytování nenalezeno'
    message = 'Toto ubytování neexistuje nebo bylo odstraněno.'
  }

  return (
    <div className="container flex min-h-[50vh] flex-col items-center justify-center py-16 text-center">
      <h1 className="mb-2 text-2xl font-semibold text-slate-700">{title}</h1>
      <p className="mb-6 max-w-sm text-slate-500">{message}</p>
      <Button asChild>
        <Link to="/ubytovani/hledat">Zpět na vyhledávání</Link>
      </Button>
    </div>
  )
}

export const Route = createFileRoute('/_layout/ubytovani/$slug/$unitSlug/galerie')({
  component: GalleryPage,
  errorComponent: GalleryErrorComponent,
  params: {
    parse: (params) => galleryParamsSchema.parse(params),
    stringify: (params) => ({ slug: params.slug, unitSlug: params.unitSlug }),
  },
  loader: async ({ context, params }) => {
    try {
      await context.queryClient.query({
        ...accommodationDetailQueryOptions(params.slug, params.unitSlug),
        staleTime: 'static',
      })
    } catch (error) {
      // An unknown slug must surface as a real 404, not an SSR 500.
      if (error instanceof HttpError && error.status === 404) {
        throw notFound()
      }
      throw error
    }
  },
  // The gallery is a photo view of the detail page; canonicalize to the detail
  // so ranking signals consolidate there instead of splitting duplicate content.
  head: ({ params }) => ({
    meta: [{ title: `Fotogalerie ubytování | ${SITE_NAME}` }],
    links: [
      {
        rel: 'canonical',
        href: `${getSiteOrigin()}/ubytovani/${params.slug}/${params.unitSlug}`,
      },
    ],
  }),
})

function GalleryPage() {
  const { slug, unitSlug } = Route.useParams()
  return <GalleryContent slug={slug} unitSlug={unitSlug} />
}
